import { Box } from '@mui/material';
import { useDashboard } from '../../../hooks/useDashboard';
import MessagePopup from '../../common/MessagePopup';
import { useState, useEffect, useRef, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';

// Add this type definition near the top after the imports
interface PromptSection {
  key: string;
  title: string;
  placeholder: string;
}

const SystemPrompt = ({ isEditable }: { isEditable: boolean }) => {
  const { localValues, handleInputChange } = useDashboard();
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Refs for managing editor state
  const isActivelyEditing = useRef(false);
  const debounceTimer = useRef<number | null>(null);
  const lastSyncedValues = useRef<string>('');

  // Step 1: Centralize the definition of your prompt sections.
  // Using useRef ensures this configuration is stable and doesn't trigger unnecessary re-renders.
  const promptSections = useRef<PromptSection[]>([
    {
      key: 'identity',
      title: 'Identity',
      placeholder: 'What persona do you want this AI to take?',
    },
    {
      key: 'firstMessage',
      title: 'Greetings',
      placeholder: 'The Opening line of AI assistant to start the conversation',
    },
    {
      key: 'context',
      title: 'Context',
      placeholder: 'Provide information about the situation or environment',
    },
    {
      key: 'job',
      title: 'Job',
      placeholder: "What's the task you want accomplished?",
    },
    {
      key: 'examples',
      title: 'Examples',
      placeholder: "Any references you'd like the AI to be guided by",
    },
    {
      key: 'constraints',
      title: 'Constraints',
      placeholder: "Boundaries you'd like the AI to stay within or go beyond",
    },
    {
      key: 'format',
      title: 'Format',
      placeholder: 'Request the answer in a table, email, list, etc.',
    },
    {
      key: 'tone',
      title: 'Tone',
      placeholder:
        'Examples: Friendly, professional, authoritative, informative, urgent',
    },
  ]).current;

  // Create a stable list of all field keys for the sync function.
  const allFieldKeys = useRef(promptSections.map(s => s.key)).current;

  // ...

  // Step 2: Update `createCombinedPrompt` to use the centralized configuration.
  const createCombinedPrompt = useCallback(() => {
    return promptSections
      .map((section: PromptSection) => {
        // Get the raw value from the parent state.
        const rawValue = localValues[section.key as keyof typeof localValues];

        // FIX: Explicitly convert the rawValue to a string.
        // This handles cases where the value might be a number or an array,
        // satisfying the linting rule.
        const value = String(rawValue || '');

        // The placeholder text now comes from our central config.
        // The rest of the line remains the same.
        return `### <span class="field-name">${section.title}</span>\n${value || `_${section.placeholder}_`}\n`;
      })
      .join('\n');
  }, [localValues, promptSections]);

  // ...

  // Step 3: Update `parseCombinedPrompt` to also use the centralized configuration.
  const parseCombinedPrompt = useCallback(
    (combinedPrompt: string) => {
      // Create a mapping from title to key from our single source of truth.
      const titleToKey = promptSections.reduce(
        (acc, section: PromptSection) => {
          acc[section.title] = section.key;
          return acc;
        },
        {} as Record<string, string>
      );

      const sections = combinedPrompt.split(/### /);
      const parsed: any = {};

      sections.forEach(section => {
        if (!section.trim()) return;

        const lines = section.split('\n');
        const title = lines[0]?.trim();
        const content = lines.slice(1).join('\n').trim();
        const key = titleToKey[title];

        // This logic remains the same, but it will be handled robustly by the new sync function.
        if (key && content && !content.startsWith('_')) {
          parsed[key] = content;
        }
      });

      return parsed;
    },
    [promptSections]
  );

  // Editor's local state - completely independent
  const [editorValue, setEditorValue] = useState(() => {
    const initial = createCombinedPrompt();
    lastSyncedValues.current = initial;
    return initial;
  });

  // Only update editor when external changes occur AND user is not editing
  useEffect(() => {
    const newValue = createCombinedPrompt();
    if (
      !isActivelyEditing.current &&
      newValue !== lastSyncedValues.current &&
      newValue !== editorValue
    ) {
      setEditorValue(newValue);
      lastSyncedValues.current = newValue;
    }
  }, [createCombinedPrompt, editorValue]); // Added editorValue to dependencies

  // Step 4: THE CORE FIX - A robust `syncToParent` function.
  const syncToParent = useCallback(
    (value: string) => {
      lastSyncedValues.current = value;

      // This object only contains keys for sections that have user-entered content.
      const parsedFieldsWithContent = parseCombinedPrompt(value);

      // FIX: Iterate over ALL possible keys to build a complete state update.
      allFieldKeys.forEach(key => {
        // If the key exists in the parsed content, use its value.
        // Otherwise, it means the user cleared it, so its new value is an empty string.
        const newValue = parsedFieldsWithContent[key] || '';

        // Get the current value from the parent state to avoid unnecessary updates.
        const currentValue = localValues[key as keyof typeof localValues] || '';

        // Only call the update handler if the value has actually changed.
        if (currentValue !== newValue) {
          handleInputChange(key as any, newValue);
        }
      });
    },
    [parseCombinedPrompt, handleInputChange, localValues, allFieldKeys]
  );

  // Debounced sync function (no changes needed here)
  const debouncedSync = useCallback(
    (value: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        syncToParent(value);
        isActivelyEditing.current = false;
      }, 500);
    },
    [syncToParent]
  );

  const handleCombinedPromptChange = useCallback(
    (value: string | undefined) => {
      const newValue = value || '';
      isActivelyEditing.current = true;
      setEditorValue(newValue);
      debouncedSync(newValue);
    },
    [debouncedSync]
  );

  // Handle when user clicks away from editor (no changes needed here)
  const handleBlur = useCallback(() => {
    if (isActivelyEditing.current) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      syncToParent(editorValue);
      isActivelyEditing.current = false;
    }
  }, [editorValue, syncToParent]);

  // Cleanup timer on unmount (no changes needed here)
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <MDEditor
        value={editorValue}
        onChange={handleCombinedPromptChange}
        preview={isEditable ? 'edit' : 'preview'}
        hideToolbar={!isEditable}
        visibleDragbar={false}
        data-color-mode="light"
        height={`calc(100vh - 140px)`}
        style={{
          backgroundColor: 'transparent',
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '14px',
        }}
        textareaProps={{
          placeholder:
            'Define your system prompt using the structured format with headers for each section...',
          style: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            lineHeight: '1.5',
          },
          onBlur: handleBlur,
        }}
      />

      <style>{`
        /* Styles remain the same */
        .w-md-editor {
          border: none !important;
          box-shadow: none !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 14px !important;
          padding: 0 !important;
        }
        .w-md-editor-text-input,
        .w-md-editor-text-container,
        .w-md-editor-text,
        .w-md-editor-preview,
        .wmde-markdown {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          font-size: 14px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
                .w-md-editor-preview h3,
        .wmde-markdown h3 {
          font-weight: 600 !important;
          color: #374151 !important;
          margin: 0.8em 0 0.4em 0 !important;
          font-size: 16px !important;
        }
        .w-md-editor-preview h3 .field-name,
        .wmde-markdown h3 .field-name {
          color: #2B2F32 !important;
        }
        .w-md-editor-preview h1,
        .w-md-editor-preview h2,
        .wmde-markdown h1,
        .wmde-markdown h2 {
          font-weight: 500 !important;
          color: #6B7280 !important;
          margin: 0.6em 0 0.3em 0 !important;
          font-size: 15px !important;
        }
      `}</style>

      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default SystemPrompt;
